import { useEffect, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import API, { authFetch } from './api';

/* ------------------------------------------------------------------ */
/* Shared helpers                                                      */
/* ------------------------------------------------------------------ */

const fitColor = (pct) => {
  if (pct >= 85) return { text: 'text-[#00E676]', bg: 'bg-[#00E676]', hex: '#00E676' };
  if (pct >= 70) return { text: 'text-[#00D4FF]', bg: 'bg-[#00D4FF]', hex: '#00D4FF' };
  if (pct >= 55) return { text: 'text-yellow-400', bg: 'bg-yellow-400', hex: '#FACC15' };
  if (pct >= 40) return { text: 'text-orange-400', bg: 'bg-orange-400', hex: '#FB923C' };
  return { text: 'text-red-400', bg: 'bg-red-400', hex: '#F87171' };
};

const tierBadgeClasses = (tier) =>
  ({
    'ELITE FIT': 'bg-emerald-900/40 text-emerald-300 border-emerald-500/30',
    'STRONG FIT': 'bg-green-900/40 text-green-300 border-green-500/30',
    'GOOD FIT': 'bg-yellow-900/40 text-yellow-300 border-yellow-500/30',
    'MARGINAL FIT': 'bg-orange-900/40 text-orange-300 border-orange-500/30',
    'POOR FIT': 'bg-red-900/40 text-red-300 border-red-500/30',
  }[tier] || 'bg-gray-800 text-gray-300 border-gray-600/30');

const tierLeftBorder = (tier) =>
  ({
    'ELITE FIT': 'border-l-4 border-l-[#00E676]',
    'STRONG FIT': 'border-l-4 border-l-[#00D4FF]',
    'GOOD FIT': 'border-l-4 border-l-yellow-400',
    'MARGINAL FIT': 'border-l-4 border-l-orange-400',
    'POOR FIT': 'border-l-4 border-l-red-400',
  }[tier] || 'border-l-4 border-l-gray-600');

const fitPct = (score9) => (score9 == null ? 0 : Math.round((score9 / 9) * 100));
const money = (v) => (v == null ? '—' : `$${Number(v).toLocaleString()}`);
// Pet policy from the structured booleans, falling back to the legacy free-text column.
const petsLine = (p) => {
  const { dogs_allowed: d, cats_allowed: c, pets_allowed: legacy } = p || {};
  if (d == null && c == null) return legacy || null;
  if (d && c) return 'Dogs & cats OK';
  if (d) return 'Dogs OK';
  if (c) return 'Cats OK';
  if (d === false && c === false) return 'No pets';
  return legacy || null;
};
const num = (v) => (v == null ? '—' : Number(v).toLocaleString());
// The meaningful expanded attributes for a tour book stop, omitting anything unstated.
const tourFeatures = (p) => [
  { label: 'Pool', value: p.pool != null ? boolStr(p.pool) : null },
  { label: 'Waterfront', value: p.waterfront != null ? boolStr(p.waterfront) : null },
  { label: 'Gated', value: p.gated_community != null ? boolStr(p.gated_community) : null },
  { label: 'Stories', value: p.stories != null ? String(p.stories) : null },
  { label: 'Pets', value: petsLine(p) },
  { label: '55+', value: p.senior_community === true ? 'Yes' : null },
  { label: 'Accessible', value: p.accessibility_features === true ? 'Yes' : null },
  { label: 'Subdivision', value: p.subdivision || null },
].filter((f) => f.value != null);
// price/sqft is derived from price and total sqft when both exist.
const pricePerSqft = (p) => (p?.price != null && p?.sqft ? p.price / p.sqft : null);
const cap = (s) =>
  s ? String(s).replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()) : '—';
const boolStr = (v) => (v === true ? 'Yes' : v === false ? 'No' : '—');

const formatDate = (iso) => {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    return d.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
  } catch (_) {
    return iso;
  }
};

const addressLine = (p) => {
  if (!p) return '';
  const cityPart = [p.city, p.state].filter(Boolean).join(', ');
  const tail = [cityPart, p.zip].filter(Boolean).join(' ');
  return [p.address, tail].filter(Boolean).join(', ');
};

const bedBath = (p) => {
  const parts = [];
  if (p?.beds != null) parts.push(`${num(p.beds)} bd`);
  if (p?.baths != null) parts.push(`${num(p.baths)} ba`);
  if (p?.sqft != null) parts.push(`${num(p.sqft)} sqft`);
  return parts.join(' · ');
};

const propertyTypeChip = (p) => {
  if (!p?.property_type) return null;
  return (
    <span className="px-2 py-1 text-xs uppercase tracking-wider rounded border bg-cyan-900/40 text-cyan-300 border-cyan-600/40">
      {cap(p.property_type)}
    </span>
  );
};

const listingStatusPill = (status) => {
  if (!status) return null;
  const map = {
    active: 'bg-green-900/40 text-green-300 border-green-600/40',
    pending: 'bg-yellow-900/40 text-yellow-300 border-yellow-600/40',
    sold: 'bg-gray-800 text-gray-300 border-gray-600/40',
    inactive: 'bg-red-900/40 text-red-300 border-red-600/40',
  };
  const cls = map[String(status).toLowerCase()] || 'bg-gray-800 text-gray-300 border-gray-600/40';
  return (
    <span className={`px-2 py-1 text-xs uppercase tracking-wider rounded border ${cls}`}>
      {cap(status)}
    </span>
  );
};

const TierPill = ({ tier }) => {
  if (!tier) return null;
  return (
    <span
      className={`inline-block px-2 py-0.5 rounded text-xs font-medium border ${tierBadgeClasses(
        tier
      )}`}
    >
      {tier}
    </span>
  );
};

/* ------------------------------------------------------------------ */
/* PrintBar                                                            */
/* ------------------------------------------------------------------ */

const PrintBar = ({ title }) => (
  <div className="bg-[#1A2744] border-b border-[#546E7A]/30 print:hidden no-print">
    <div className="max-w-5xl mx-auto px-4 md:px-6 py-3 flex items-center justify-between gap-4">
      <Link
        to="/reports"
        className="flex items-center gap-2 text-gray-300 hover:text-[#00D4FF] text-sm font-medium transition-colors shrink-0"
      >
        ← Back
      </Link>
      {title && (
        <div className="hidden md:block flex-1 min-w-0 text-center text-white text-sm font-semibold truncate">
          {title}
        </div>
      )}
      <button
        onClick={() => window.print()}
        className="px-4 py-2 bg-[#FF6B35] text-white font-semibold rounded-lg hover:bg-[#E65A28] transition-colors text-sm whitespace-nowrap shrink-0"
      >
        🖨 <span className="hidden sm:inline">Print / Save as PDF</span><span className="sm:hidden">Print</span>
      </button>
    </div>
  </div>
);

/* ------------------------------------------------------------------ */
/* Print CSS                                                           */
/* ------------------------------------------------------------------ */

const PRINT_CSS = `
@media print {
  @page { size: letter; margin: 0.75in; }
  html, body { background: white !important; color: black !important; }
  nav, .no-print { display: none !important; }
  [class*="bg-[#1A2744]"], [class*="bg-[#0A0E27]"], [class*="bg-[#1A1A2E]"] {
    background: white !important;
    color: #1A1A1A !important;
    border-color: #E5E7EB !important;
  }
  [class*="text-white"], [class*="text-gray-200"], [class*="text-gray-300"], [class*="text-gray-400"] {
    color: #1A1A1A !important;
  }
  [class*="text-gray-500"], [class*="text-gray-600"] {
    color: #6B7280 !important;
  }
  /* Strip link styling on paper — cyan underlined links look bad in print. */
  a, a:hover, a:visited { color: inherit !important; text-decoration: none !important; }
  .card { break-inside: avoid; }
  .page-break { break-before: page; }
  * { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
}
`;

/* ------------------------------------------------------------------ */
/* Link helpers — degrade to a plain fragment if the target id is      */
/* missing, so data without ids still renders cleanly.                 */
/* ------------------------------------------------------------------ */
const PropertyLink = ({ property, className = '', children }) => {
  if (!property?.id) return <>{children}</>;
  return (
    <Link
      to={`/properties/${property.id}`}
      className={`text-[#00D4FF] hover:text-[#00E676] hover:underline transition-colors ${className}`}
    >
      {children}
    </Link>
  );
};

const BuyerLink = ({ buyer, className = '', children }) => {
  if (!buyer?.id) return <>{children}</>;
  return (
    <Link
      to={`/match-board?buyer=${buyer.id}`}
      className={`text-[#00D4FF] hover:text-[#00E676] hover:underline transition-colors ${className}`}
    >
      {children}
    </Link>
  );
};

const EvalLink = ({ propertyId, buyerId, className = '', children, title }) => {
  if (!propertyId || !buyerId) return <>{children}</>;
  return (
    <Link
      to={`/properties/${propertyId}/buyer/${buyerId}`}
      className={`hover:opacity-90 transition-opacity inline-block ${className}`}
      title={title || 'Open home evaluation'}
    >
      {children}
    </Link>
  );
};

/* ------------------------------------------------------------------ */
/* Reusable atoms                                                      */
/* ------------------------------------------------------------------ */

const Card = ({ children, className = '', accent = '' }) => (
  <div
    className={`card bg-[#1A2744] rounded-xl border border-[#546E7A]/30 p-5 md:p-6 ${accent} ${className}`}
  >
    {children}
  </div>
);

const SectionHeader = ({ children, className = '' }) => (
  <h2 className={`text-white font-semibold text-lg ${className}`}>{children}</h2>
);

const Label = ({ children }) => (
  <div className="text-gray-500 text-xs uppercase tracking-wider">{children}</div>
);

const SpecValue = ({ children }) => (
  <div className="text-white font-bold text-lg">{children}</div>
);

const Spec = ({ label, value }) => (
  <div>
    <Label>{label}</Label>
    <SpecValue>{value ?? '—'}</SpecValue>
  </div>
);

const FitBar = ({ score9, color, showValue = true }) => {
  const p = fitPct(score9);
  const c = color || fitColor(p);
  return (
    <div className="flex items-center gap-3">
      <div className="flex-1 bg-gray-800 rounded h-3 overflow-hidden print:bg-gray-200">
        <div className={`${c.bg} h-full rounded`} style={{ width: `${p}%` }} />
      </div>
      {showValue && (
        <div className={`${c.text} font-semibold text-sm w-16 text-right`}>
          {score9 == null ? '—' : `${p}%`}
        </div>
      )}
    </div>
  );
};

/* ------------------------------------------------------------------ */
/* Property Flyer                                                      */
/* ------------------------------------------------------------------ */

const PropertyFlyer = ({ data }) => {
  const p = data.property || {};
  const agentName = data.listing_agent?.name;

  const hasSchools = [
    p.school_district, p.school_rating, p.elementary_school, p.middle_school, p.high_school,
  ].some((v) => v != null && v !== '');

  // Only attributes the listing actually states appear on the flyer. A field left
  // unstated is omitted rather than printed as "No" — a marketing piece must not assert
  // that a home lacks something the listing never addressed.
  const waterfrontDetail = p.waterfront_features || p.waterfront_type;
  const featureRows = [
    { label: 'Pool', value: p.pool != null ? boolStr(p.pool) : null },
    { label: 'Pool Features', value: p.pool_features ? cap(p.pool_features) : null },
    { label: 'Waterfront', value: p.waterfront ? (waterfrontDetail ? cap(waterfrontDetail) : 'Yes') : (p.waterfront === false ? 'No' : null) },
    { label: 'Gated Community', value: p.gated_community != null ? boolStr(p.gated_community) : null },
    { label: 'Senior Community', value: p.senior_community === true ? 'Yes' : null },
    { label: 'Accessibility', value: p.accessibility_features === true ? 'Yes' : null },
    { label: 'Stories', value: p.stories != null ? p.stories : null },
    { label: 'Full / Half Baths', value: (p.bathrooms_full != null || p.bathrooms_half != null)
        ? `${p.bathrooms_full ?? '—'} full / ${p.bathrooms_half ?? '—'} half` : null },
    { label: 'Furnished', value: p.furnished ? cap(p.furnished) : null },
    { label: 'Pets', value: petsLine(p) },
    { label: 'Cooling', value: p.cooling ? cap(p.cooling) : null },
    { label: 'Heating', value: p.heating ? cap(p.heating) : null },
    { label: 'Appliances', value: p.appliances || null },
    { label: 'Amenities', value: p.community_amenities || null },
  ].filter((r) => r.value != null);

  const factRows = [
    { label: 'Property Type', value: p.property_type ? cap(p.property_type) : null },
    { label: 'Status', value: p.listing_status ? cap(p.listing_status) : null },
    { label: 'MLS #', value: p.mls_number || null },
    { label: 'Original List Price', value: p.original_list_price != null ? money(p.original_list_price) : null },
    { label: 'County', value: p.county || null },
    { label: 'Subdivision', value: p.subdivision || null },
    { label: 'Faces', value: p.direction_faces ? cap(p.direction_faces) : null },
  ].filter((r) => r.value != null);

  return (
    <div className="space-y-5">
      {/* Header */}
      <Card accent="border-l-4 border-l-[#FF6B35]">
        <h1 className="text-white text-3xl md:text-4xl font-bold leading-tight">
          <PropertyLink property={p} className="hover:underline">
            {p.address || 'Home'}
          </PropertyLink>
        </h1>
        <div className="text-gray-400 mt-2 text-base">{addressLine(p)}</div>
        <div className="flex flex-wrap gap-2 mt-4">
          {propertyTypeChip(p)}
          {listingStatusPill(p.listing_status)}
        </div>
      </Card>

      {/* Main layout */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        {/* LEFT col */}
        <div className="lg:col-span-2 space-y-5">
          <Card accent="border-l-4 border-l-[#FF6B35]">
            <SectionHeader className="mb-4">Key Details</SectionHeader>
            {p.price != null && (
              <div className="mb-5 pb-5 border-b border-gray-700 flex flex-wrap items-end gap-8">
                <div>
                  <Label>Price</Label>
                  <div className="text-[#00E676] font-bold text-3xl md:text-4xl leading-tight">{money(p.price)}</div>
                  {pricePerSqft(p) != null && (
                    <div className="text-gray-400 text-xs mt-0.5">{money(Math.round(pricePerSqft(p)))} / sqft</div>
                  )}
                </div>
              </div>
            )}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <Spec label="Beds" value={p.beds ?? '—'} />
              <Spec label="Baths" value={p.baths ?? '—'} />
              <Spec label="Sqft" value={num(p.sqft)} />
              <Spec label="Year Built" value={p.year_built || '—'} />
            </div>
          </Card>

          <Card>
            <SectionHeader className="mb-4">Additional Details</SectionHeader>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <Spec label="Lot Size" value={p.lot_size_acres != null ? `${p.lot_size_acres} ac` : '—'} />
              <Spec label="Garage" value={p.garage_spaces != null ? `${p.garage_spaces} car` : '—'} />
              <Spec label="Stories" value={p.stories || '—'} />
              <Spec label="HOA" value={p.hoa_monthly != null ? `${money(p.hoa_monthly)}/mo` : '—'} />
            </div>
          </Card>

          {hasSchools && (
            <Card>
              <SectionHeader className="mb-4">Schools & Neighborhood</SectionHeader>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                <Spec label="School District" value={p.school_district || '—'} />
                <Spec label="School Rating" value={p.school_rating != null ? `${p.school_rating}/10` : '—'} />
                <Spec label="Elementary" value={p.elementary_school || '—'} />
                <Spec label="Middle" value={p.middle_school || '—'} />
                <Spec label="High" value={p.high_school || '—'} />
                <Spec label="Subdivision" value={p.subdivision || '—'} />
              </div>
            </Card>
          )}

          {featureRows.length > 0 && (
            <Card>
              <SectionHeader className="mb-4">Home Features</SectionHeader>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-2">
                {featureRows.map((r) => (
                  <div key={r.label} className="flex justify-between text-sm border-b border-gray-800 pb-1.5">
                    <span className="text-gray-400">{r.label}</span>
                    <span className="text-white font-semibold text-right ml-3">{r.value}</span>
                  </div>
                ))}
              </div>
            </Card>
          )}

          {p.description && (
            <Card>
              <SectionHeader className="mb-3">Description</SectionHeader>
              <div className="text-gray-200 text-sm leading-relaxed whitespace-pre-line">
                {p.description}
              </div>
            </Card>
          )}
        </div>

        {/* RIGHT col */}
        <div className="space-y-5">
          {factRows.length > 0 && (
            <Card>
              <SectionHeader className="mb-4">Listing Facts</SectionHeader>
              <div className="space-y-2">
                {factRows.map((r) => (
                  <div key={r.label} className="flex justify-between text-sm gap-3">
                    <span className="text-gray-400 shrink-0">{r.label}</span>
                    <span className="text-white font-semibold text-right">{r.value}</span>
                  </div>
                ))}
              </div>
            </Card>
          )}

          {agentName && (
            <Card>
              <Label>Listing Agent</Label>
              <div className="text-white font-bold text-lg mt-1">{agentName}</div>
              {p.listing_url && (
                <div className="mt-2 text-sm">
                  <a href={p.listing_url} target="_blank" rel="noreferrer" className="text-[#00D4FF] hover:text-[#00E676] hover:underline transition-colors break-all">
                    View MLS listing
                  </a>
                </div>
              )}
            </Card>
          )}
        </div>
      </div>
    </div>
  );
};

/* ------------------------------------------------------------------ */
/* Showing Notes — open text, phone-friendly, shared buyer + agent     */
/* ------------------------------------------------------------------ */

const noteDraftKey = (buyerId, propertyId) => `tourbook-note-draft:${buyerId}:${propertyId}`;
const readDraft = (key) => {
  try { return window.localStorage.getItem(key) || ''; } catch (_) { return ''; }
};
const writeDraft = (key, value) => {
  try {
    if (value) window.localStorage.setItem(key, value);
    else window.localStorage.removeItem(key);
  } catch (_) { /* storage unavailable — draft just isn't kept */ }
};
const noteAuthorLabel = (n) => {
  const role = n.author_role === 'buyer' ? 'Buyer'
    : n.author_role === 'broker' ? 'Broker'
    : n.author_role === 'super_admin' ? 'Admin'
    : 'Agent';
  return n.author_name ? `${n.author_name} · ${role}` : role;
};
const noteTime = (iso) => {
  const d = new Date(String(iso).replace(' ', 'T'));
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleString([], { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
};
// 16px text keeps iOS Safari from zooming in when the box is tapped.
const noteInputCls =
  'w-full px-3 py-2 bg-[#0A0E27] border border-[#546E7A]/50 rounded-lg text-white text-base placeholder-gray-600 focus:outline-none focus:ring-2 focus:ring-[#00D4FF]';

const ShowingNotes = ({ buyerId, propertyId, initialNotes }) => {
  const draftKey = noteDraftKey(buyerId, propertyId);
  const [notes, setNotes] = useState(initialNotes || []);
  const [draft, setDraft] = useState(() => readDraft(draftKey));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [editText, setEditText] = useState('');

  const updateDraft = (v) => { setDraft(v); writeDraft(draftKey, v); };

  const readError = async (res, fallback) => {
    try {
      const d = await res.json();
      return typeof d.detail === 'string' ? d.detail : fallback;
    } catch (_) { return fallback; }
  };

  const addNote = async () => {
    if (!draft.trim() || saving) return;
    setSaving(true);
    setError('');
    try {
      const res = await authFetch(`${API}/api/notes`, {
        method: 'POST',
        body: JSON.stringify({ property_id: propertyId, buyer_id: buyerId, note_text: draft.trim() }),
      });
      if (!res.ok) { setError(await readError(res, 'Could not save the note. Please try again.')); return; }
      const saved = await res.json();
      setNotes((prev) => [saved, ...prev]);
      updateDraft('');
    } catch (_) {
      setError('No connection — your note is kept on this device. Tap Save again when you have signal.');
    } finally {
      setSaving(false);
    }
  };

  const saveEdit = async (id) => {
    if (!editText.trim()) return;
    setError('');
    try {
      const res = await authFetch(`${API}/api/notes/${id}`, {
        method: 'PUT',
        body: JSON.stringify({ note_text: editText.trim() }),
      });
      if (!res.ok) { setError(await readError(res, 'Could not update the note.')); return; }
      const saved = await res.json();
      setNotes((prev) => prev.map((n) => (n.id === id ? saved : n)));
      setEditingId(null);
    } catch (_) {
      setError('No connection — please try again.');
    }
  };

  const deleteNote = async (id) => {
    if (!window.confirm('Delete this note?')) return;
    setError('');
    try {
      const res = await authFetch(`${API}/api/notes/${id}`, { method: 'DELETE' });
      if (!res.ok) { setError(await readError(res, 'Could not delete the note.')); return; }
      setNotes((prev) => prev.filter((n) => n.id !== id));
    } catch (_) {
      setError('No connection — please try again.');
    }
  };

  return (
    <Card>
      <div className="flex items-baseline justify-between gap-2 mb-3">
        <SectionHeader>Showing Notes</SectionHeader>
        {notes.length > 0 && (
          <span className="text-gray-500 text-xs no-print">
            {notes.length} note{notes.length !== 1 ? 's' : ''}
          </span>
        )}
      </div>

      {/* Write a note (screen only) */}
      <div className="no-print print:hidden mb-4">
        <textarea
          value={draft}
          onChange={(e) => updateDraft(e.target.value)}
          placeholder="What stood out? Kitchen, yard, noise, light, neighborhood…"
          rows={4}
          className={`${noteInputCls} resize-y`}
        />
        <div className="flex items-center justify-between gap-3 mt-2">
          <span className="text-gray-500 text-xs">
            Shared between buyer and agent · used in the AI report
          </span>
          <button
            onClick={addNote}
            disabled={saving || !draft.trim()}
            className="px-5 py-2.5 bg-[#00E676] text-[#0A0E27] font-semibold rounded-lg hover:bg-[#00C853] disabled:opacity-50 transition-colors text-sm shrink-0"
          >
            {saving ? 'Saving…' : 'Save Note'}
          </button>
        </div>
        {error && <div className="text-red-400 text-sm mt-2">{error}</div>}
      </div>

      {/* Saved notes */}
      {notes.length > 0 && (
        <ul className="space-y-2">
          {notes.map((n) => (
            <li key={n.id} className="p-3 bg-[#0A0E27]/50 rounded-lg card">
              <div className="flex items-start justify-between gap-2">
                <div className="text-xs text-gray-400">
                  <span className={n.author_role === 'buyer' ? 'text-cyan-300' : 'text-green-300'}>
                    {noteAuthorLabel(n)}
                  </span>
                  <span className="text-gray-500"> · {noteTime(n.created_at)}</span>
                </div>
                {n.can_edit && editingId !== n.id && (
                  <div className="flex gap-3 shrink-0 no-print print:hidden">
                    <button
                      onClick={() => { setEditingId(n.id); setEditText(n.note_text); }}
                      className="text-gray-400 hover:text-[#00D4FF] text-xs py-1"
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => deleteNote(n.id)}
                      className="text-gray-400 hover:text-red-400 text-xs py-1"
                    >
                      Delete
                    </button>
                  </div>
                )}
              </div>
              {editingId === n.id ? (
                <div className="mt-2 no-print print:hidden">
                  <textarea
                    value={editText}
                    onChange={(e) => setEditText(e.target.value)}
                    rows={3}
                    className={`${noteInputCls} resize-y`}
                  />
                  <div className="flex justify-end gap-2 mt-2">
                    <button onClick={() => setEditingId(null)} className="px-3 py-2 text-gray-300 text-sm">
                      Cancel
                    </button>
                    <button
                      onClick={() => saveEdit(n.id)}
                      disabled={!editText.trim()}
                      className="px-4 py-2 bg-[#00D4FF] text-[#0A0E27] font-semibold rounded-lg disabled:opacity-50 text-sm"
                    >
                      Save
                    </button>
                  </div>
                </div>
              ) : (
                <p className="text-gray-200 text-sm mt-1 whitespace-pre-wrap break-words">{n.note_text}</p>
              )}
            </li>
          ))}
        </ul>
      )}

      {/* Blank lines for handwriting on the printed copy */}
      <div className="hidden print:block space-y-3 mt-4">
        {Array.from({ length: notes.length > 0 ? 4 : 8 }).map((_, idx) => (
          <div key={idx} className="border-b border-gray-400 h-6" />
        ))}
      </div>
    </Card>
  );
};

/* ------------------------------------------------------------------ */
/* Tour Book                                                           */
/* ------------------------------------------------------------------ */

const TourBook = ({ data }) => {
  const properties = data.properties || [];
  const buyer = data.buyer || {};
  const agent = data.agent || {};

  return (
    <div className="space-y-6">
      {/* Cover */}
      <div className="card bg-[#1A1A2E] rounded-xl border border-[#546E7A]/30 p-8 md:p-12 text-center">
        <div className="text-gray-400 text-xs uppercase tracking-[0.3em] mb-3">
          Home Tour Book
        </div>
        <div
          className="mx-auto mb-6"
          style={{ borderTop: '3px solid #FF6B35', width: '80px' }}
        />
        <h1 className="text-white text-4xl md:text-5xl font-bold mb-3" style={{ fontFamily: 'Georgia, serif' }}>
          {buyer?.id ? (
            <Link
              to={`/match-board?buyer=${buyer.id}`}
              style={{ color: '#FFFFFF' }}
              className="hover:text-[#00E676] hover:underline transition-colors"
            >
              {buyer.name || 'Buyer'}
            </Link>
          ) : (buyer.name || 'Buyer')}
        </h1>
        {agent?.name && (
          <div className="text-gray-300 text-base mt-4">Prepared by {agent.name}</div>
        )}
        {data.date_iso && (
          <div className="text-gray-400 text-sm mt-1">{formatDate(data.date_iso)}</div>
        )}
      </div>

      {/* TOC */}
      <Card>
        <SectionHeader className="mb-4">Table of Contents</SectionHeader>
        <ol className="space-y-2">
          {properties.map((entry, i) => {
            const p = entry.property || {};
            const fp = fitPct(entry.fit_score);
            const c = fitColor(fp);
            return (
              <li key={p.id || i} className="flex items-center gap-3">
                <span className="text-gray-500 w-6 text-sm font-mono">{i + 1}.</span>
                <a
                  href={`#prop-${p.id}`}
                  className="flex-1 text-white hover:text-[#00D4FF] truncate"
                >
                  {p.address || 'Home'}
                </a>
                <span className={`${c.text} font-semibold text-sm`}>{fp}%</span>
              </li>
            );
          })}
        </ol>
      </Card>

      {/* Per-property */}
      {properties.map((entry, i) => {
        const p = entry.property || {};
        const fp = fitPct(entry.fit_score);
        const c = fitColor(fp);
        const dims = entry.dimension_scores || {};

        return (
          <div key={p.id || i} id={`prop-${p.id}`} className="page-break space-y-5">
            <Card>
              <div className="flex flex-wrap items-start justify-between gap-4">
                <div className="flex-1 min-w-0">
                  <div className="text-xs text-gray-500 uppercase tracking-wider mb-1">
                    Stop #{i + 1}
                  </div>
                  <h2 className="text-white text-2xl md:text-3xl font-bold">
                    <PropertyLink property={p} className="hover:underline">
                      {p.address || 'Home'}
                    </PropertyLink>
                  </h2>
                  <div className="text-gray-400 mt-1">{addressLine(p)}</div>
                  <div className="flex flex-wrap gap-2 mt-3">
                    {propertyTypeChip(p)}
                    {listingStatusPill(p.listing_status)}
                  </div>
                </div>
                <EvalLink propertyId={p.id} buyerId={buyer?.id} className="text-right">
                  <div className={`${c.text} text-5xl font-bold leading-none`}>{fp}%</div>
                  <div className="mt-2">
                    <TierPill tier={entry.tier} />
                  </div>
                </EvalLink>
              </div>
            </Card>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
              <Card className="lg:col-span-2">
                <SectionHeader className="mb-4">Key Details</SectionHeader>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <Spec label="Beds" value={p.beds ?? '—'} />
                  <Spec label="Baths" value={p.baths ?? '—'} />
                  <Spec label="Sqft" value={num(p.sqft)} />
                  <Spec label="Year Built" value={p.year_built || '—'} />
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-4">
                  <Spec label="Lot Size" value={p.lot_size_acres != null ? `${p.lot_size_acres} ac` : '—'} />
                  <Spec label="Garage" value={p.garage_spaces != null ? `${p.garage_spaces} car` : '—'} />
                  <Spec label="Schools" value={p.school_rating != null ? `${p.school_rating}/10` : '—'} />
                  <Spec label="HOA" value={p.hoa_monthly != null ? `${money(p.hoa_monthly)}/mo` : '—'} />
                </div>
                {/* Stated lifestyle attributes only — an unstated field is left out rather
                    than shown as a dash, so the tour book never implies a home lacks
                    something the listing simply didn't mention. */}
                {tourFeatures(p).length > 0 && (
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-4 pt-4 border-t border-gray-700">
                    {tourFeatures(p).map((f) => (
                      <Spec key={f.label} label={f.label} value={f.value} />
                    ))}
                  </div>
                )}
              </Card>

              <Card accent="border-l-4 border-l-[#FF6B35]">
                <Label>Price</Label>
                <div className="text-[#00E676] font-bold text-2xl mt-1">
                  {p.price != null ? money(p.price) : '—'}
                </div>
                {p.price != null && pricePerSqft(p) != null && (
                  <div className="text-gray-400 text-xs mt-1">
                    {money(Math.round(pricePerSqft(p)))} / sqft
                  </div>
                )}
                {p.school_district && (
                  <div className="text-gray-400 text-xs mt-2">{p.school_district}</div>
                )}
              </Card>
            </div>

            {/* Category Scores */}
            {Object.keys(dims).length > 0 && (
              <Card>
                <SectionHeader className="mb-4">Category Scores</SectionHeader>
                <div className="space-y-3">
                  {Object.entries(dims).map(([name, score]) => {
                    const sp = fitPct(score);
                    const sc = fitColor(sp);
                    return (
                      <div key={name} className="grid grid-cols-12 gap-3 items-center">
                        <div className="col-span-4 text-gray-300 text-sm">{name}</div>
                        <div className="col-span-8">
                          <FitBar score9={score} color={sc} />
                        </div>
                      </div>
                    );
                  })}
                </div>
              </Card>
            )}

            {/* Strengths / Concerns */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <Card accent="border-l-4 border-l-[#00E676]">
                <SectionHeader className="mb-3 text-[#00E676]">Top Strengths</SectionHeader>
                {entry.top_strengths?.length ? (
                  <ul className="space-y-2">
                    {entry.top_strengths.map((s, idx) => (
                      <li key={idx} className="flex items-start justify-between gap-2 text-sm">
                        <div>
                          <div className="text-white">{s.name}</div>
                          {s.category && (
                            <div className="text-gray-500 text-xs">{s.category}</div>
                          )}
                        </div>
                        <span className="text-[#00E676] font-semibold whitespace-nowrap">
                          {fitPct(s.score)}%
                        </span>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <div className="text-gray-500 text-sm">No data</div>
                )}
              </Card>

              <Card accent="border-l-4 border-l-[#FF6B35]">
                <SectionHeader className="mb-3 text-[#FF6B35]">Top Concerns</SectionHeader>
                {entry.top_concerns?.length ? (
                  <ul className="space-y-2">
                    {entry.top_concerns.map((s, idx) => (
                      <li key={idx} className="flex items-start justify-between gap-2 text-sm">
                        <div>
                          <div className="text-white">{s.name}</div>
                          {s.category && (
                            <div className="text-gray-500 text-xs">{s.category}</div>
                          )}
                        </div>
                        <span className="text-orange-400 font-semibold whitespace-nowrap">
                          {fitPct(s.score)}%
                        </span>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <div className="text-gray-500 text-sm">No data</div>
                )}
              </Card>
            </div>

            {/* Showing Notes — typed on a phone during the tour; blank lines on paper */}
            {p.id && buyer?.id ? (
              <ShowingNotes buyerId={buyer.id} propertyId={p.id} initialNotes={entry.notes} />
            ) : null}
          </div>
        );
      })}

      {/* Final summary */}
      {properties.length > 0 && (
        <Card>
          <SectionHeader className="mb-4">Side-by-Side Summary</SectionHeader>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-gray-400 text-left border-b border-gray-700">
                  <th className="px-2 py-2 font-medium">#</th>
                  <th className="px-2 py-2 font-medium">Address</th>
                  <th className="px-2 py-2 font-medium">Fit</th>
                  <th className="px-2 py-2 font-medium">Price</th>
                  <th className="px-2 py-2 font-medium">Bd/Ba</th>
                  <th className="px-2 py-2 font-medium">Sqft</th>
                  <th className="px-2 py-2 font-medium">Year</th>
                </tr>
              </thead>
              <tbody>
                {properties.map((entry, idx) => {
                  const p = entry.property || {};
                  const fp = fitPct(entry.fit_score);
                  const c = fitColor(fp);
                  const isBest = idx === 0;
                  return (
                    <tr
                      key={p.id || idx}
                      className={`${idx % 2 === 0 ? 'bg-[#0A0E27]/40' : ''} ${
                        isBest ? 'bg-[#00E676]/10' : ''
                      }`}
                    >
                      <td className="px-2 py-2 text-gray-400">{idx + 1}</td>
                      <td className="px-2 py-2 text-white">
                        <PropertyLink property={p} className="hover:underline">
                          {p.address || '—'}
                        </PropertyLink>
                      </td>
                      <td className={`px-2 py-2 font-semibold ${c.text}`}>
                        <EvalLink propertyId={p.id} buyerId={buyer?.id}>
                          {fp}%
                        </EvalLink>
                      </td>
                      <td className="px-2 py-2 text-gray-200">{money(p.price)}</td>
                      <td className="px-2 py-2 text-gray-200">
                        {p.beds ?? '—'}/{p.baths ?? '—'}
                      </td>
                      <td className="px-2 py-2 text-gray-200">{num(p.sqft)}</td>
                      <td className="px-2 py-2 text-gray-200">{p.year_built || '—'}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </Card>
      )}
    </div>
  );
};

/* ------------------------------------------------------------------ */
/* Comparison                                                          */
/* ------------------------------------------------------------------ */

const COMPARISON_METRICS = [
  { key: 'price', label: 'Price', best: 'price', fmt: (p) => money(p.price) },
  {
    key: 'price_per_sqft',
    label: 'Price/Sqft',
    best: null,
    fmt: (p) => (pricePerSqft(p) == null ? '—' : money(Math.round(pricePerSqft(p)))),
  },
  { key: 'beds', label: 'Beds', best: 'beds', fmt: (p) => (p.beds ?? '—') },
  { key: 'baths', label: 'Baths', best: 'baths', fmt: (p) => (p.baths ?? '—') },
  { key: 'sqft', label: 'Sqft', best: 'sqft', fmt: (p) => num(p.sqft) },
  {
    key: 'lot_size_acres',
    label: 'Lot Size',
    best: 'lot_size_acres',
    fmt: (p) => (p.lot_size_acres == null ? '—' : `${p.lot_size_acres} ac`),
  },
  { key: 'year_built', label: 'Year Built', best: 'year_built', fmt: (p) => p.year_built || '—' },
  {
    key: 'school_rating',
    label: 'School Rating',
    best: 'school_rating',
    fmt: (p) => (p.school_rating == null ? '—' : `${p.school_rating}/10`),
  },
  {
    key: 'garage_spaces',
    label: 'Garage',
    best: 'garage_spaces',
    fmt: (p) => (p.garage_spaces == null ? '—' : `${p.garage_spaces} car`),
  },
  {
    key: 'hoa_monthly',
    label: 'HOA / mo',
    best: 'hoa_monthly',
    fmt: (p) => (p.hoa_monthly == null ? '—' : `${money(p.hoa_monthly)}`),
  },
  {
    key: 'property_type',
    label: 'Type',
    best: null,
    fmt: (p) => (p.property_type ? cap(p.property_type) : '—'),
  },
];

const COMPARISON_COLORS = ['#00D4FF', '#00E676', '#FF6B35', '#9C27B0', '#FFC107'];

const Comparison = ({ data }) => {
  const properties = data.properties || [];
  const bestByField = data.best_index_by_field || {};
  const buyer = data.buyer || {};

  const categoryNames = properties[0]?.dimension_scores
    ? Object.keys(properties[0].dimension_scores)
    : [];

  return (
    <div className="space-y-5">
      <Card accent="border-l-4 border-l-[#00E676]">
        <h1 className="text-white text-3xl md:text-4xl font-bold">{data.title || 'Home Comparison'}</h1>
        <div className="text-gray-400 mt-2">
          For{' '}
          <span className="text-white font-semibold">
            <BuyerLink buyer={buyer} className="!text-white">{buyer.name || '—'}</BuyerLink>
          </span>
          {data.date_iso && <span className="text-gray-500 ml-2">· {formatDate(data.date_iso)}</span>}
        </div>
      </Card>

      {/* Top stat row */}
      <Card>
        <SectionHeader className="mb-4">Fit Score Overview</SectionHeader>
        <div
          className="grid gap-4"
          style={{
            gridTemplateColumns: `repeat(${Math.min(properties.length, 5)}, minmax(0, 1fr))`,
          }}
        >
          {properties.map((entry, idx) => {
            const p = entry.property || {};
            const fp = fitPct(entry.fit_score);
            const c = fitColor(fp);
            const isBestFit = bestByField.fit_score === idx;
            return (
              <div key={p.id || idx} className="text-center">
                <div className="text-gray-400 text-xs truncate" title={p.address}>
                  <PropertyLink property={p} className="hover:underline">
                    {p.address || '—'}
                  </PropertyLink>
                </div>
                <EvalLink propertyId={p.id} buyerId={buyer?.id}>
                  <div className={`${c.text} text-4xl font-bold mt-1`}>{fp}%</div>
                  <div className="mt-1">
                    <TierPill tier={entry.tier} />
                  </div>
                </EvalLink>
                {isBestFit && (
                  <div className="text-[#00E676] text-xs font-semibold mt-1">★ BEST FIT</div>
                )}
              </div>
            );
          })}
        </div>
      </Card>

      {/* Property cards grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
        {properties.map((entry, idx) => {
          const p = entry.property || {};
          const fp = fitPct(entry.fit_score);
          const c = fitColor(fp);
          return (
            <Card key={p.id || idx}>
              <div className="mb-3">
                <h3 className="text-white font-bold text-base truncate">
                  <PropertyLink property={p} className="hover:underline">
                    {p.address || 'Home'}
                  </PropertyLink>
                </h3>
                <div className="text-gray-500 text-xs truncate">{addressLine(p)}</div>
                <div className="flex flex-wrap gap-1.5 mt-2">
                  {propertyTypeChip(p)}
                  {listingStatusPill(p.listing_status)}
                </div>
              </div>

              <EvalLink propertyId={p.id} buyerId={buyer?.id} className="block">
                <div className="flex items-center justify-between border-y border-gray-700 py-3 mb-3">
                  <div>
                    <Label>Fit Score</Label>
                    <div className={`${c.text} text-3xl font-bold`}>{fp}%</div>
                  </div>
                  <TierPill tier={entry.tier} />
                </div>
              </EvalLink>

              <div className="space-y-1.5">
                {COMPARISON_METRICS.map((m) => {
                  const isBest = m.best && bestByField[m.best] === idx;
                  return (
                    <div key={m.key} className="flex justify-between items-center text-sm">
                      <span className="text-gray-400">{m.label}</span>
                      <span
                        className={`font-semibold flex items-center gap-1 ${
                          isBest ? 'text-[#00E676]' : 'text-white'
                        }`}
                      >
                        {m.fmt(p)}
                        {isBest && <span className="text-[#00E676]">✓</span>}
                      </span>
                    </div>
                  );
                })}
              </div>
            </Card>
          );
        })}
      </div>

      {/* Category Score Comparison */}
      {categoryNames.length > 0 && (
        <Card>
          <SectionHeader className="mb-3">Category Score Comparison</SectionHeader>
          <div className="flex flex-wrap gap-3 mb-4 text-xs">
            {properties.map((entry, idx) => {
              const p = entry.property || {};
              const color = COMPARISON_COLORS[idx % COMPARISON_COLORS.length];
              return (
                <div key={p.id || idx} className="flex items-center gap-2">
                  <span
                    className="inline-block w-3 h-3 rounded"
                    style={{ background: color }}
                  />
                  <span className="text-gray-300 truncate max-w-[140px]">
                    <PropertyLink property={p} className="!text-gray-300 hover:!text-[#00D4FF]">
                      {p.address || '—'}
                    </PropertyLink>
                  </span>
                </div>
              );
            })}
          </div>

          <div className="space-y-5">
            {categoryNames.map((cat) => (
              <div key={cat}>
                <div className="text-white text-sm font-semibold mb-2">{cat}</div>
                <div className="space-y-1.5">
                  {properties.map((entry, idx) => {
                    const color = COMPARISON_COLORS[idx % COMPARISON_COLORS.length];
                    const score = entry.dimension_scores?.[cat];
                    const sp = fitPct(score);
                    return (
                      <div key={idx} className="flex items-center gap-3">
                        <div className="w-3 h-3 rounded shrink-0" style={{ background: color }} />
                        <div className="flex-1 bg-gray-800 rounded h-3 overflow-hidden print:bg-gray-200">
                          <div
                            className="h-full rounded"
                            style={{ width: `${sp}%`, background: color }}
                          />
                        </div>
                        <div className="text-white text-sm font-semibold w-16 text-right">
                          {score == null ? '—' : `${sp}%`}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}
    </div>
  );
};

/* ------------------------------------------------------------------ */
/* Match Summary                                                       */
/* ------------------------------------------------------------------ */

const CRITERIA_COLORS = ['#00D4FF', '#00E676', '#FF6B35', '#9C27B0', '#FFC107'];

const MatchSummary = ({ data }) => {
  const buyer = data.buyer || {};
  const criteria = data.criteria_weights || [];
  const ranked = data.ranked_properties || [];

  return (
    <div className="space-y-5">
      <Card accent="border-l-4 border-l-[#9C27B0]">
        <h1 className="text-white text-3xl md:text-4xl font-bold">{data.title || 'Match Summary'}</h1>
        <div className="text-gray-400 mt-2">
          Buyer:{' '}
          <span className="text-white font-semibold">
            <BuyerLink buyer={buyer} className="!text-white">{buyer.name || '—'}</BuyerLink>
          </span>
          {data.date_iso && (
            <span className="text-gray-500 ml-2">· {formatDate(data.date_iso)}</span>
          )}
        </div>
      </Card>

      {criteria.length > 0 && (
        <Card>
          <SectionHeader className="mb-4">Buyer Criteria Weights</SectionHeader>
          <div className="space-y-3">
            {criteria.map((c, i) => {
              const weightPct = Math.round((c.weight || 0) * 100);
              const color = CRITERIA_COLORS[i % CRITERIA_COLORS.length];
              return (
                <div key={c.name} className="grid grid-cols-12 gap-3 items-center">
                  <div className="col-span-4 text-gray-300 text-sm">{c.name}</div>
                  <div className="col-span-7">
                    <div className="bg-gray-800 rounded h-3 overflow-hidden print:bg-gray-200">
                      <div
                        className="h-full rounded"
                        style={{ width: `${weightPct}%`, background: color }}
                      />
                    </div>
                  </div>
                  <div className="col-span-1 text-white text-sm font-semibold text-right">
                    {weightPct}%
                  </div>
                </div>
              );
            })}
          </div>
        </Card>
      )}

      <div>
        <SectionHeader className="mb-3">Ranked Homes</SectionHeader>
        <div className="space-y-3">
          {ranked.map((entry, idx) => {
            const p = entry.property || {};
            const fp = fitPct(entry.fit_score);
            const c = fitColor(fp);
            const isTop = entry.rank === 1 || idx === 0;
            const accent = isTop ? 'border-l-4 border-l-[#FF6B35]' : tierLeftBorder(entry.tier);
            return (
              <Card key={p.id || idx} accent={accent}>
                <div className="flex items-center gap-4">
                  <div className="text-gray-500 text-2xl font-bold w-12 text-center shrink-0">
                    #{entry.rank || idx + 1}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h3 className="text-white font-bold text-base truncate">
                        <PropertyLink property={p} className="hover:underline">
                          {p.address || 'Home'}
                        </PropertyLink>
                      </h3>
                      {isTop && (
                        <span className="px-2 py-0.5 rounded text-xs font-semibold border bg-orange-900/40 text-orange-300 border-orange-500/30">
                          TOP MATCH
                        </span>
                      )}
                    </div>
                    <div className="text-gray-500 text-xs truncate mt-0.5">
                      {addressLine(p)}
                    </div>
                    <div className="text-gray-400 text-xs mt-1">
                      {bedBath(p)}
                      {p.year_built ? ` · Built ${p.year_built}` : ''}
                      {p.property_type ? ` · ${cap(p.property_type)}` : ''}
                    </div>
                  </div>
                  <EvalLink propertyId={p.id} buyerId={buyer?.id} className="text-right shrink-0">
                    <div className={`${c.text} text-3xl font-bold leading-none`}>{fp}%</div>
                    <div className="mt-2">
                      <TierPill tier={entry.tier} />
                    </div>
                    <div className="text-gray-300 text-xs mt-2">{money(p.price)}</div>
                  </EvalLink>
                </div>
              </Card>
            );
          })}
        </div>
      </div>
    </div>
  );
};

/* ------------------------------------------------------------------ */
/* Listing Interest                                                    */
/* ------------------------------------------------------------------ */

const ListingInterest = ({ data }) => {
  const p = data.property || {};
  const agentName = data.listing_agent?.name;
  const clients = data.matching_clients || [];

  return (
    <div className="space-y-5">
      <Card accent="border-l-4 border-l-yellow-400">
        <h1 className="text-white text-3xl md:text-4xl font-bold">
          <PropertyLink property={p} className="hover:underline">
            {p.address || 'Home'}
          </PropertyLink>
        </h1>
        <div className="text-gray-400 mt-2">{addressLine(p)}</div>
        <div className="flex flex-wrap gap-2 mt-3">
          {propertyTypeChip(p)}
          {listingStatusPill(p.listing_status)}
        </div>
        {data.date_iso && (
          <div className="text-gray-500 text-xs mt-3">{formatDate(data.date_iso)}</div>
        )}
      </Card>

      {/* Property strip */}
      <Card>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Spec label="Beds" value={p.beds ?? '—'} />
          <Spec label="Baths" value={p.baths ?? '—'} />
          <Spec label="Sqft" value={num(p.sqft)} />
          <Spec label="Year Built" value={p.year_built || '—'} />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 border-t border-gray-700 mt-4 pt-4">
          <div>
            <Label>Price</Label>
            <div className="text-[#00E676] font-bold text-2xl mt-1">
              {p.price != null ? money(p.price) : '—'}
            </div>
          </div>
          {agentName && (
            <div className="md:text-right">
              <Label>Listing Agent</Label>
              <div className="text-white font-semibold mt-1">{agentName}</div>
            </div>
          )}
        </div>
      </Card>

      {/* Matching buyers header */}
      <div className="flex items-center justify-between">
        <SectionHeader>Matching Buyers</SectionHeader>
        <span className="px-3 py-1 rounded-full bg-[#1A2744] border border-[#00D4FF]/30 text-[#00D4FF] text-sm font-semibold">
          {clients.length} buyer{clients.length === 1 ? '' : 's'}
        </span>
      </div>

      <div className="space-y-3">
        {clients.map((entry, idx) => {
          const buyer = entry.buyer || {};
          const fp = fitPct(entry.fit_score);
          const c = fitColor(fp);
          return (
            <Card key={buyer.id || idx} accent={tierLeftBorder(entry.tier)}>
              <div className="flex flex-col md:flex-row md:items-center gap-4">
                <div className="flex-1 min-w-0">
                  <div className="text-white font-bold text-lg">
                    <BuyerLink buyer={buyer} className="!text-white hover:!text-[#00E676]">
                      {buyer.name || '—'}
                    </BuyerLink>
                  </div>
                  <div className="mt-2">
                    <FitBar score9={entry.fit_score} color={c} showValue={false} />
                  </div>
                </div>
                <EvalLink propertyId={p.id} buyerId={buyer?.id} className="md:text-right md:w-32">
                  <div className={`${c.text} text-3xl font-bold leading-none`}>{fp}%</div>
                  <div className="mt-2">
                    <TierPill tier={entry.tier} />
                  </div>
                </EvalLink>
              </div>

              {entry.top_criteria?.length > 0 && (
                <div className="mt-4 border-t border-gray-700 pt-3">
                  <Label>Top Matching Criteria</Label>
                  <div className="mt-2 space-y-1.5">
                    {entry.top_criteria.map((crit, i) => {
                      const cp = fitPct(crit.score);
                      const cc = fitColor(cp);
                      return (
                        <div key={i} className="flex items-center justify-between text-sm">
                          <div className="min-w-0 flex-1">
                            <span className="text-white">{crit.name}</span>
                            {crit.category && (
                              <span className="text-gray-500 text-xs ml-2">{crit.category}</span>
                            )}
                          </div>
                          <span
                            className={`px-2 py-0.5 rounded text-xs font-semibold ${cc.text} bg-gray-800/60 print:bg-gray-100`}
                          >
                            {cp}%
                          </span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </Card>
          );
        })}
        {clients.length === 0 && (
          <Card>
            <div className="text-gray-400 text-sm text-center py-4">
              No buyers have scored this home yet.
            </div>
          </Card>
        )}
      </div>
    </div>
  );
};

/* ------------------------------------------------------------------ */
/* Main viewer                                                         */
/* ------------------------------------------------------------------ */

export default function ReportViewer() {
  const [searchParams] = useSearchParams();
  const type = searchParams.get('type');
  const propertyId = searchParams.get('property_id');
  const buyerId = searchParams.get('buyer_id');
  const propertyIds = searchParams.get('property_ids');

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const loadReport = async () => {
      setLoading(true);
      setError(null);
      setData(null);

      let endpoint = null;
      if (type === 'flyer') {
        if (!propertyId) { setError('Missing property_id'); setLoading(false); return; }
        endpoint = `${API}/api/reports/property-flyer/${propertyId}`;
      } else if (type === 'tour-book') {
        if (!buyerId) { setError('Missing buyer_id'); setLoading(false); return; }
        endpoint = `${API}/api/reports/tour-book/${buyerId}`;
      } else if (type === 'comparison') {
        if (!buyerId || !propertyIds) { setError('Missing buyer_id or property_ids'); setLoading(false); return; }
        endpoint = `${API}/api/reports/comparison/${buyerId}?property_ids=${propertyIds}`;
      } else if (type === 'match-summary') {
        if (!buyerId) { setError('Missing buyer_id'); setLoading(false); return; }
        endpoint = `${API}/api/reports/match-summary/${buyerId}`;
      } else if (type === 'listing-interest') {
        if (!propertyId) { setError('Missing property_id'); setLoading(false); return; }
        endpoint = `${API}/api/reports/listing-interest/${propertyId}`;
      } else {
        setError(`Unknown report type: ${type || '(none)'}`);
        setLoading(false);
        return;
      }

      try {
        const res = await authFetch(endpoint);
        if (!res.ok) {
          let msg = `Failed to load report (${res.status})`;
          try {
            const errData = await res.json();
            if (errData.detail) msg = typeof errData.detail === 'string' ? errData.detail : msg;
          } catch (_) { /* ignore */ }
          if (!cancelled) { setError(msg); setLoading(false); }
          return;
        }
        const json = await res.json();
        if (!cancelled) { setData(json); setLoading(false); }
      } catch (err) {
        console.error('Report load error:', err);
        if (!cancelled) { setError('Network error. Please try again.'); setLoading(false); }
      }
    };
    loadReport();
    return () => { cancelled = true; };
  }, [type, propertyId, buyerId, propertyIds]);

  // Set document title for nicer print/save
  useEffect(() => {
    const prev = document.title;
    if (data?.title) document.title = data.title;
    return () => { document.title = prev; };
  }, [data?.title]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0A0E27] flex items-center justify-center">
        <div className="text-[#00D4FF] text-lg">Loading report…</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-[#0A0E27] flex items-center justify-center">
        <div className="bg-[#1A2744] rounded-xl border border-red-600/40 p-8 text-center max-w-md">
          <div className="text-red-400 text-lg font-semibold mb-2">Could Not Load Report</div>
          <div className="text-gray-300 text-sm mb-4">{error}</div>
          <Link
            to="/reports"
            className="inline-block px-4 py-2 bg-[#00D4FF] text-[#0A0E27] font-semibold rounded-lg hover:bg-[#00B8D4] text-sm"
          >
            ← Back to Reports
          </Link>
        </div>
      </div>
    );
  }

  if (!data) return null;

  return (
    <div className="min-h-screen bg-[#0A0E27] text-white print:bg-white print:text-black">
      <style>{PRINT_CSS}</style>
      <PrintBar title={data.title} />
      <div className="max-w-5xl mx-auto px-4 md:px-6 py-8 print:p-0 print:max-w-none">
        {type === 'flyer' && <PropertyFlyer data={data} />}
        {type === 'tour-book' && <TourBook data={data} />}
        {type === 'comparison' && <Comparison data={data} />}
        {type === 'match-summary' && <MatchSummary data={data} />}
        {type === 'listing-interest' && <ListingInterest data={data} />}
      </div>
    </div>
  );
}
